import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box} from 'views/components';
import {WeeklyCalendarUsers} from 'views/module';
import {Translate, translate} from 'views/module';

import UserRow from './UserRow';
import EventTile from './EventTile';

import backend from 'utils/backend';
import moment from 'moment';

const CaregiverScheduleDashboard = (props) => {
  const progressWrap = props.statusProgressWrap;

  const [caregivers, setCaregivers] = React.useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = React.useState(undefined);
  const [loadingCaregivers, setLoadingCaregivers] = React.useState(false);

  const userFilter = [
    {
      key: 'SCHEDULED_FOR_THIS_WEEK',
      label: translate('caregiver.CaregiverCalendar.Calendar.Filter.User.ScheduleForThisWeek'),
      filter: (user, scheduleEvents, weekDays) => {
        const hasOneEvent = weekDays.some((day) => {
          const dateStr = day.format('YYYY-MM-DD');
          return scheduleEvents?.[dateStr]?.length > 0;
        });
        return hasOneEvent;
      },
      defaultEnabled: true,
    },
  ];
  const eventFilter = [
    {
      key: 'HAS_NOT_CONFIRMED_SHIFT',
      label: translate('caregiver.CaregiverCalendar.Calendar.Filter.Event.NotConfirmedShift'),
      filter: (user, event) => {
        const confirmedShift = event.caregiverUpdates.some((e) => e.type === 'shiftConfirmation');
        return !confirmedShift;
      },
    },
    {
      key: 'HAS_NOT_CLOCKED_IN',
      label: translate('caregiver.CaregiverCalendar.Calendar.Filter.Event.NotClockedIn'),
      filter: (user, event) => {
        const clockedIn = event.caregiverUpdates.some((e) => e.type === 'clockInConfirmation');
        return !clockedIn;
      },
    },
    {
      key: 'HAS_NOT_CLOCKED_OUT',
      label: translate('caregiver.CaregiverCalendar.Calendar.Filter.Event.NotClockedOut'),
      filter: (user, event) => {
        const clockedOut = event.caregiverUpdates.some((e) => e.type === 'clockOutConfirmation');
        return !clockedOut;
      },
    },
    {
      key: 'HAS_CLOCKED_IN_LATE',
      label: translate('caregiver.CaregiverCalendar.Calendar.Filter.Event.LateClockin'),
      filter: (user, event) => {
        const clockedIn = event.caregiverUpdates.find((e) => e.type === 'clockInConfirmation');
        if (!clockedIn) {
          return false;
        }

        const isLate = moment(clockedIn.data.actualClockinTimeFormatted).isAfter(event.eventData.startTime);
        return isLate;
      },
    },
    {
      key: 'HAS_CLOCKED_OUT_LATE',
      label: translate('caregiver.CaregiverCalendar.Calendar.Filter.Event.EarlyClockout'),
      filter: (user, event) => {
        const clockedOut = event.caregiverUpdates.find((e) => e.type === 'clockInConfirmation');
        if (!clockedOut) {
          return false;
        }

        const isLate = moment(event.eventData.endTime).isAfter(clockedOut.data.actualClockoutTimeFormatted);
        return isLate;
      },
    },
  ];


  React.useEffect(() => {
    if (lastEvaluatedKey === null) {
      return;
    }
    progressWrap(async () => {
      setLoadingCaregivers(true);
      const filter = [
        {
          key: 'status',
          value: 'ACTIVE',
        },
      ];

      const sortBy = {
        sortKey: 'createdAt',
        sortDirection: 'DESC',
      };

      const result = await backend.caregiver.queryCaregiver(
          filter,
          sortBy,
          lastEvaluatedKey,
      );
      setCaregivers((e) => {
        return [
          ...e,
          ...result.list,
        ];
      });

      setLastEvaluatedKey(result.lastEvaluatedKey);
      setLoadingCaregivers(false);
    }, {showProgress: false});
  }, [lastEvaluatedKey]);

  const loadUserSchedules = async (user, startDay, endDay) => {
    return new Promise((resolve, reject) => {
      progressWrap(async () => {
        const start = moment(startDay).startOf('day').toISOString();
        const end = moment(endDay).endOf('day').toISOString();

        const {events} = await backend.caregiver.schedule.loadByIsoRange(user.caregiverRn, start, end, true);
        const scheduleByDay = events.reduce((acc, event) => {
          const dateKey = moment(event.eventData.startTime).format('YYYY-MM-DD');
          return {
            ...acc,
            [dateKey]: [
              ...(acc[dateKey] || []),
              event,
            ],
          };
        }, {});
        Object.keys(scheduleByDay).forEach((date) => {
          scheduleByDay[date].sort((a, b) => {
            const timeA = moment(a.eventData.startTime);
            const timeB = moment(b.eventData.startTime);
            return timeA.diff(timeB);
          });
        });
        resolve(scheduleByDay);
      }, {showProgress: false});
    });
  };

  return (
    <Box
      style={{
        maxWidth: '100%',
        overflowX: 'auto',
      }}
    >
      <Box
        style={{
          minWidth: '1184px',
          maxWidth: '2200px',
          width: '100%',
        }}
      >
        <WeeklyCalendarUsers
          headerContent={<Translate expression='caregiver.CaregiverCalendar.Calendar.Details.Title' />}
          users={caregivers}
          getUserRn={(user) => user.caregiverRn}
          loadUserSchedules={loadUserSchedules}
          UserRow={UserRow}
          EventTile={EventTile}
          loadingUsers={loadingCaregivers && caregivers.length === 0}
          userFilter={userFilter}
          eventFilter={eventFilter}
        />
      </Box>
    </Box>
  );
};

CaregiverScheduleDashboard.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(CaregiverScheduleDashboard));
